import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, X, List, LayoutGrid } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import FilterPills from "./admin/FilterPills";
import LaneKpiStrip, { bucketMatches, type KpiBucket } from "./lanes/LaneKpiStrip";
import LaneTable from "./lanes/LaneTable";
import LaneFloor from "./lanes/LaneFloor";
import LaneDetailDrawer from "./lanes/LaneDetailDrawer";
import { useLanes, STALE_THRESHOLD_MS, type Lane } from "./lanes/useLanes";
import type { StatusSlug } from "./lanes/laneStatus";
import "../styles/lanes.css";

type LanesView = "table" | "floor";

const VIEW_STORAGE_KEY = "lanes-view";

const readView = (): LanesView => {
    try {
        const v = window.localStorage.getItem(VIEW_STORAGE_KEY);
        if (v === "floor") return "floor";
    } catch { /* ignore */ }
    return "table";
};

const writeView = (view: LanesView) => {
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, view); } catch { /* ignore */ }
};

const KPI_LABEL_KEYS: Record<KpiBucket, string> = {
    total: "lanes_kpi_total",
    "on-auction": "lanes_kpi_on_auction",
    "closing-today": "lanes_kpi_closing_today",
    outbound: "lanes_kpi_outbound",
    idle: "lanes_kpi_idle",
    unprocessed: "lanes_kpi_unprocessed",
};

const LanesAdmin = () => {
    const { t } = useTranslation();
    const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
    const [kpiBucket, setKpiBucket] = useState<KpiBucket | null>(null);
    const [selectedLane, setSelectedLane] = useState<Lane | null>(null);
    const [now, setNow] = useState<number>(Date.now());
    const [view, setView] = useState<LanesView>(() => readView());

    const switchView = (next: LanesView) => {
        setView(next);
        writeView(next);
    };

    const { lanes, allLanes, warehouses, refresh, refreshing, initialLoaded, lastUpdatedAt, error, setLaneStatus } =
        useLanes(selectedWarehouse || undefined);

    // Apply a manual status change and keep the open drawer's snapshot in
    // sync so the dropdown reflects the new value immediately.
    const handleStatusChange = (barcode: string, status: StatusSlug) => {
        setLaneStatus(barcode, status);
        setSelectedLane(prev => (prev && prev.barcode === barcode ? { ...prev, status } : prev));
    };

    // Surface fetch failures once per error. Last-good lanes stay on
    // screen — that's the PWA contract.
    useEffect(() => {
        if (error) toast.error(t("server_error"), { toastId: "lanes-load-err" });
    }, [error, t]);

    // Wall-clock tick so the Stale tag appears without a refresh.
    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);

    // Pill count badges show each warehouse's total lane count using
    // the unfiltered set, so the badge stays stable when the user picks
    // a different warehouse.
    const pillsWithCounts = useMemo(
        () => warehouses.map(w => ({
            value: w,
            label: w,
            count: allLanes.filter(l => l.warehouse === w).length,
        })),
        [warehouses, allLanes],
    );

    const visibleLanes = useMemo(() => {
        if (kpiBucket === null) return lanes;
        return lanes.filter(l => bucketMatches(l, kpiBucket));
    }, [lanes, kpiBucket]);

    const isStale = lastUpdatedAt !== null && (now - lastUpdatedAt) > STALE_THRESHOLD_MS;

    return (
        <div className="lanes-admin">
            <LaneKpiStrip lanes={lanes} active={kpiBucket} onChange={setKpiBucket} />

            <div className="lanes-toolbar">
                <div className="lanes-toolbar-left">
                    <FilterPills
                        options={pillsWithCounts}
                        value={selectedWarehouse}
                        onChange={setSelectedWarehouse}
                        labelKey="lanes_warehouse_filter"
                        allLabelKey="status_all"
                        allCount={allLanes.length}
                    />
                </div>
                <div className="lanes-toolbar-right">
                    <div className="lanes-segmented" role="tablist" aria-label={t("lanes_view_table")}>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === "table"}
                            className={`lanes-segmented-btn${view === "table" ? " lanes-segmented-btn--active" : ""}`}
                            onClick={() => switchView("table")}
                        >
                            <List size={14} strokeWidth={2} />
                            {t("lanes_view_table")}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === "floor"}
                            className={`lanes-segmented-btn${view === "floor" ? " lanes-segmented-btn--active" : ""}`}
                            onClick={() => switchView("floor")}
                        >
                            <LayoutGrid size={14} strokeWidth={2} />
                            {t("lanes_view_floor")}
                        </button>
                    </div>
                    <span className="lanes-demo-tag" title={t("lanes_demo_data")}>
                        {t("lanes_demo_data")}
                    </span>
                    {isStale && <span className="lanes-stale-tag">{t("lanes_stale")}</span>}
                    <button
                        type="button"
                        className="lanes-refresh-btn"
                        onClick={refresh}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} strokeWidth={2} className={refreshing ? "icon-spin" : ""} />
                        {t("lanes_refresh")}
                    </button>
                </div>
            </div>

            {kpiBucket !== null && (
                <div className="lanes-filter-chip-row">
                    <span className="lanes-filter-chip">
                        {t("lanes_filter_chip_prefix")}: {t(KPI_LABEL_KEYS[kpiBucket])}
                        <button
                            type="button"
                            className="lanes-filter-chip-close"
                            onClick={() => setKpiBucket(null)}
                            aria-label={t("lanes_clear_filter")}
                        >
                            <X size={14} strokeWidth={2} />
                        </button>
                    </span>
                </div>
            )}

            {!initialLoaded && (
                <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
                    <Loader2 size={24} className="dispatch-admin-spin" />
                </div>
            )}

            {initialLoaded && view === "table" && (
                <LaneTable
                    lanes={visibleLanes}
                    warehouseId={selectedWarehouse}
                    onLaneClick={setSelectedLane}
                    onStatusChange={handleStatusChange}
                />
            )}

            {initialLoaded && view === "floor" && (
                <LaneFloor
                    lanes={visibleLanes}
                    onLaneClick={setSelectedLane}
                />
            )}

            <LaneDetailDrawer lane={selectedLane} onClose={() => setSelectedLane(null)} onStatusChange={handleStatusChange} />
        </div>
    );
};

export default LanesAdmin;
